import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent, ReactNode } from 'react';
import {
  ArrowLeft,
  Baby,
  BookMarked,
  ChartColumn,
  ClipboardList,
  Check,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Coins,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Layers3,
  LogIn,
  LogOut,
  PackagePlus,
  Pencil,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Store,
  Trash2,
  Trophy
} from 'lucide-react';
import type {
  AppState,
  Child,
  KnowledgeItem,
  KnowledgeType,
  LearningPlansByChild,
  LearningStatus,
  Product,
  ProfilePage,
  RewardConfig
} from '../types';
import { ChildAvatar, Modal, SectionTitle, Segmented } from '../components/ui';
import { LearningPlanManager } from '../components/LearningPlanManager';
import { createBackup, login, lookupPoem, lookupWordImages } from '../lib/storage';
import {
  formatBirthMonth,
  formatDateTime,
  getAgeText,
  getChildProgress,
  getDueCount,
  getItemStatus,
  getLearningPlanChildIds,
  getPlanCount,
  makeId
} from '../lib/srs';

interface ProfileProps {
  state: AppState;
  activeChildId: string | null;
  page: ProfilePage;
  loggedIn: boolean;
  updateState: (updater: (current: AppState) => AppState) => void;
  onNavigate: (page: ProfilePage) => void;
  onSelectChild: (childId: string) => void;
  onLoginChange: (value: boolean) => void;
  onToast: (message: string) => void;
}

const statusMeta: Record<LearningStatus, { label: string; className: string }> = {
  unlearned: { label: '未学', className: 'is-gray' },
  learning: { label: '学习中', className: 'is-blue' },
  mastered: { label: '已学', className: 'is-green' }
};

const typeLabel: Record<KnowledgeType, string> = {
  character: '汉字',
  poem: '古诗',
  word: '英文单词'
};

const avatarChoices = ['🐰', '🦊', '🐼', '🐯', '🐨', '🐵', '🐧', '🦁'];
const avatarColors = ['#ff8c73', '#2f9d88', '#7c6bd6', '#e6a51f', '#3987c9', '#d45f8b'];
function unionLearningPlan(plans: LearningPlansByChild) {
  return [...new Set(Object.values(plans).flat())];
}
const productIcons: Record<string, string> = {
  贴纸: '⭐',
  铅笔: '✏️',
  绘本: '📚',
  书: '📚',
  野餐: '🧺',
  电影: '🎬',
  游戏: '🧩',
  冰淇淋: '🍦',
  玩具: '🪁',
  蛋糕: '🍰',
  公园: '🌳'
};

export function ProfileView(props: ProfileProps) {
  if (!props.loggedIn) {
    return <LoginPage onLoginChange={props.onLoginChange} onToast={props.onToast} />;
  }

  return (
    <div className="profile-view">
      {props.page === 'menu' ? <ProfileMenu {...props} /> : null}
      {props.page === 'children' ? <ChildrenManager {...props} /> : null}
      {props.page === 'knowledge' ? <KnowledgeManager {...props} /> : null}
      {props.page === 'plan' ? (
        <LearningPlanManager
          state={props.state}
          updateState={props.updateState}
          onToast={props.onToast}
          onBack={() => props.onNavigate('menu')}
        />
      ) : null}
      {props.page === 'shop' ? <StarShop {...props} /> : null}
      {props.page === 'shopRecords' ? <ShopRecords {...props} /> : null}
      {props.page === 'settings' ? <SettingsPage {...props} /> : null}
      {props.page === 'curves' ? <CurveSettings {...props} /> : null}
      {props.page === 'rewards' ? <RewardSettings {...props} /> : null}
      {props.page === 'shopSettings' ? <ShopSettings {...props} /> : null}
    </div>
  );
}

export function LoginPage({
  onLoginChange,
  onToast
}: {
  onLoginChange: (value: boolean) => void;
  onToast: (message: string) => void;
}) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(username, password);
    setLoading(false);
    if (result.ok) {
      onLoginChange(true);
      onToast('欢迎回来，家长管理员');
    } else {
      setError(result.message);
    }
  };

  return (
    <section className="login-page">
      <div className="login-page__scene" aria-hidden="true">
        <span className="login-scene__cloud login-scene__cloud--one" />
        <span className="login-scene__cloud login-scene__cloud--two" />
        <div className="login-mascot">
          <span>🌱</span>
          <i>✦</i>
        </div>
        <div className="login-stars">★ ★ ★</div>
      </div>
      <form className="login-card" onSubmit={handleSubmit}>
        <span className="login-card__badge"><ShieldCheck size={18} /> 家长入口</span>
        <h1>登录记忆岛</h1>
        <p>登录后可以管理儿童、知识库、遗忘曲线和星星商店。</p>
        <label>
          <span>用户名</span>
          <div className="input-with-icon">
            <CircleUserRound size={19} />
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              placeholder="请输入用户名"
            />
          </div>
        </label>
        <label>
          <span>密码</span>
          <div className="input-with-icon">
            <ShieldCheck size={19} />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="请输入密码"
            />
            <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label="显示或隐藏密码">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary-button primary-button--wide" type="submit" disabled={loading}>
          <LogIn size={19} />
          {loading ? '正在登录...' : '登录'}
        </button>
      </form>
    </section>
  );
}

function ProfileMenu({ state, onNavigate, onLoginChange, onToast }: ProfileProps) {
  const childrenStars = state.children.reduce((sum, child) => sum + child.stars, 0);
  const totalMastered = state.progress.filter((entry) => entry.masteredAt).length;
  const totalReviews = state.progress.reduce((sum, entry) => sum + entry.attempts, 0);

  const menuItems: Array<{ page: ProfilePage; title: string; note: string; icon: typeof Baby; tone: string }> = [
    { page: 'children', title: '儿童管理', note: `${state.children.length}/2 位儿童`, icon: Baby, tone: 'coral' },
    { page: 'knowledge', title: '知识库', note: `${state.knowledge.length} 条知识`, icon: BookMarked, tone: 'blue' },
    { page: 'plan', title: '学习计划', note: `${getPlanCount(state, undefined, state.activeChildId ?? undefined)} 项已安排`, icon: ClipboardList, tone: 'mint' },
    { page: 'shop', title: '星星商店', note: `${state.products.filter((item) => item.active).length} 件上架商品`, icon: Store, tone: 'gold' },
    { page: 'shopRecords', title: '兑换记录', note: `${state.exchanges.length} 条兑换记录`, icon: ChartColumn, tone: 'mint' },
    { page: 'settings', title: '设置', note: '曲线、奖励和商店设置', icon: Settings, tone: 'purple' }
  ];

  return (
    <>
      <section className="profile-header">
        <div>
          <span className="eyebrow">家长管理空间</span>
          <h1>我的记忆岛</h1>
          <p>管理两个孩子各自独立的学习旅程。</p>
        </div>
        <div className="profile-header__mascot" aria-hidden="true">🌱</div>
      </section>

      <section className="profile-overview">
        <article>
          <span><Star size={20} fill="currentColor" /></span>
          <small>全部星星</small>
          <strong>{childrenStars}</strong>
        </article>
        <article>
          <span><Trophy size={20} /></span>
          <small>已掌握知识</small>
          <strong>{totalMastered}</strong>
        </article>
        <article>
          <span><RefreshCcw size={20} /></span>
          <small>累计复习</small>
          <strong>{totalReviews}</strong>
        </article>
      </section>

      {state.children.length ? (
        <section className="profile-children-strip">
          {state.children.map((child) => {
            const mastered = state.progress.filter((entry) => entry.childId === child.id && entry.masteredAt).length;
            return (
              <article key={child.id}>
                <ChildAvatar child={child} size="large" />
                <div>
                  <strong>{child.name}</strong>
                  <small>{getAgeText(child.birthMonth)} · 掌握 {mastered} 项</small>
                </div>
                <span><Star size={16} fill="currentColor" /> {child.stars}</span>
              </article>
            );
          })}
        </section>
      ) : null}

      <section className="panel profile-menu">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <button key={item.page} type="button" onClick={() => onNavigate(item.page)}>
              <span className={`profile-menu__icon profile-menu__icon--${item.tone}`}><Icon size={22} /></span>
              <span>
                <strong>{item.title}</strong>
                <small>{item.note}</small>
              </span>
              <ChevronRight size={20} />
            </button>
          );
        })}
      </section>

      <section className="panel profile-actions">
        <button type="button" onClick={() => {
          createBackup(state);
          onToast('备份文件已生成');
        }}>
          <Save size={19} />
          导出数据备份
        </button>
        <button
          className="danger-text"
          type="button"
          onClick={() => {
            onLoginChange(false);
            onToast('已退出登录');
          }}
        >
          <LogOut size={19} />
          退出登录
        </button>
      </section>
    </>
  );
}

function ChildrenManager({ state, updateState, onToast, onNavigate }: ProfileProps) {
  const [editing, setEditing] = useState<Child | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Child | null>(null);

  const openNew = () => {
    if (state.children.length >= 2) {
      onToast('最多支持 2 名儿童');
      return;
    }
    setEditing(null);
  };

  const saveChild = (payload: { name: string; birthMonth: string; avatarEmoji: string; avatarColor: string }) => {
    if (editing?.id) {
      updateState((current) => ({
        ...current,
        children: current.children.map((child) => child.id === editing.id ? { ...child, ...payload } : child),
        updatedAt: new Date().toISOString()
      }));
      onToast('儿童信息已更新');
    } else {
      const child: Child = {
        id: makeId('child'),
        ...payload,
        stars: 0,
        createdAt: new Date().toISOString()
      };
      updateState((current) => ({
        ...current,
        children: [...current.children, child],
        learningPlansByChild: {
          ...(current.learningPlansByChild ?? {}),
          [child.id]: []
        },
        activeChildId: current.activeChildId || child.id,
        updatedAt: new Date().toISOString()
      }));
      onToast('儿童已添加');
    }
    setEditing(undefined);
  };

  const deleteChild = () => {
    if (!deleteTarget) return;
    updateState((current) => {
      const children = current.children.filter((child) => child.id !== deleteTarget.id);
      const learningPlansByChild = Object.fromEntries(
        Object.entries(current.learningPlansByChild ?? {}).filter(([childId]) => childId !== deleteTarget.id)
      );
      return {
        ...current,
        children,
        learningPlansByChild,
        learningPlan: unionLearningPlan(learningPlansByChild),
        progress: current.progress.filter((entry) => entry.childId !== deleteTarget.id),
        exchanges: current.exchanges.filter((entry) => entry.childId !== deleteTarget.id),
        activeChildId: current.activeChildId === deleteTarget.id ? children[0]?.id ?? null : current.activeChildId,
        updatedAt: new Date().toISOString()
      };
    });
    onToast('儿童及关联学习记录已删除');
    setDeleteTarget(null);
  };

  return (
    <ProfileSubPage
      title="儿童管理"
      subtitle={`已添加 ${state.children.length}/2 位儿童`}
      icon={<Baby size={22} />}
      onBack={() => onNavigate('menu')}
    >
      <div className="children-grid">
        {state.children.map((child, index) => {
          const started = state.progress.filter((entry) => entry.childId === child.id).length;
          const mastered = state.progress.filter((entry) => entry.childId === child.id && entry.masteredAt).length;
          return (
            <article className="child-card" key={child.id} style={{ '--card-tone': index ? '#2f9d88' : '#ff8c73' } as CSSProperties}>
              <div className="child-card__top">
                <ChildAvatar child={child} size="large" />
                <div>
                  <strong>{child.name}</strong>
                  <small>{getAgeText(child.birthMonth)}</small>
                </div>
                <span><Star size={16} fill="currentColor" />{child.stars}</span>
              </div>
              <dl>
                <div><dt>出生年月</dt><dd>{formatBirthMonth(child.birthMonth)}</dd></div>
                <div><dt>学习进度</dt><dd>{mastered}/{started || 0} 项已掌握</dd></div>
                <div><dt>账号创建</dt><dd>{formatDateTime(child.createdAt)}</dd></div>
              </dl>
              <div className="child-card__actions">
                <button className="secondary-button" type="button" onClick={() => setEditing(child)}>
                  <Pencil size={17} />修改
                </button>
                <button className="danger-button" type="button" onClick={() => setDeleteTarget(child)}>
                  <Trash2 size={17} />删除
                </button>
              </div>
            </article>
          );
        })}
        {state.children.length < 2 ? (
          <button className="add-child-placeholder" type="button" onClick={openNew}>
            <Plus size={28} />
            <strong>添加儿童</strong>
            <small>自动生成专属卡通头像</small>
          </button>
        ) : null}
      </div>

      <ChildEditor
        open={editing !== undefined}
        child={editing || null}
        onClose={() => setEditing(undefined)}
        onSave={saveChild}
      />

      <Modal
        open={Boolean(deleteTarget)}
        title={`删除 ${deleteTarget?.name || ''}`}
        subtitle="该儿童的学习进度和兑换记录也会一并删除，且无法恢复。"
        onClose={() => setDeleteTarget(null)}
        footer={
          <>
            <button className="secondary-button" type="button" onClick={() => setDeleteTarget(null)}>取消</button>
            <button className="danger-button" type="button" onClick={deleteChild}>确认删除</button>
          </>
        }
      >
        <div className="danger-note">
          <Trash2 size={24} />
          <span>请确认不再需要这名儿童的学习数据。</span>
        </div>
      </Modal>
    </ProfileSubPage>
  );
}

function ChildEditor({
  open,
  child,
  onClose,
  onSave
}: {
  open: boolean;
  child: Child | null;
  onClose: () => void;
  onSave: (payload: { name: string; birthMonth: string; avatarEmoji: string; avatarColor: string }) => void;
}) {
  const [name, setName] = useState('');
  const [birthMonth, setBirthMonth] = useState('2021-01');
  const [avatarEmoji, setAvatarEmoji] = useState('🐰');
  const [avatarColor, setAvatarColor] = useState('#ff8c73');

  useEffect(() => {
    if (!open) return;
    setName(child?.name || '');
    setBirthMonth(child?.birthMonth || '2021-01');
    setAvatarEmoji(child?.avatarEmoji || avatarChoices[Math.floor(Math.random() * avatarChoices.length)]);
    setAvatarColor(child?.avatarColor || avatarColors[Math.floor(Math.random() * avatarColors.length)]);
  }, [open, child]);

  return (
    <Modal
      open={open}
      title={child ? '修改儿童信息' : '添加儿童'}
      subtitle="儿童资料只用于展示和学习记录归档"
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button" type="button" onClick={onClose}>取消</button>
          <button
            className="primary-button"
            type="button"
            disabled={!name.trim() || !birthMonth}
            onClick={() => onSave({ name: name.trim(), birthMonth, avatarEmoji, avatarColor })}
          >
            保存
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label className="form-field">
          <span>名字</span>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：小满" maxLength={12} />
        </label>
        <label className="form-field">
          <span>出生年月</span>
          <input type="month" value={birthMonth} onChange={(event) => setBirthMonth(event.target.value)} />
        </label>
      </div>
      <div className="avatar-editor">
        <span>头像</span>
        <div className="avatar-editor__preview" style={{ '--avatar-color': avatarColor } as CSSProperties}>{avatarEmoji}</div>
        <div className="avatar-options">
          {avatarChoices.map((emoji) => (
            <button
              key={emoji}
              className={emoji === avatarEmoji ? 'is-active' : ''}
              type="button"
              onClick={() => setAvatarEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
        <div className="color-options">
          {avatarColors.map((color) => (
            <button
              key={color}
              className={color === avatarColor ? 'is-active' : ''}
              type="button"
              style={{ background: color }}
              onClick={() => setAvatarColor(color)}
              aria-label={`选择颜色 ${color}`}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

function KnowledgeManager({ state, activeChildId, updateState, onToast, onNavigate }: ProfileProps) {
  const [type, setType] = useState<KnowledgeType>('character');
  const [status, setStatus] = useState<LearningStatus>('unlearned');
  const [childFilter, setChildFilter] = useState<string>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<KnowledgeItem | null>(null);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(48);

  useEffect(() => {
    setVisibleCount(48);
  }, [type, status, childFilter, query]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return state.knowledge.filter((item) => {
      if (item.type !== type) return false;
      if (needle) {
        const searchable = [item.title, item.meaning, item.pronunciation, item.author, item.dynasty]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(needle)) return false;
      }
      if (childFilter !== 'all') {
        const progress = getChildProgress(state, childFilter, item.id);
        const childStatus = !progress ? 'unlearned' : progress.masteredAt ? 'mastered' : 'learning';
        return childStatus === status;
      }
      return getItemStatus(state, item.id).status === status;
    }).sort((left, right) => {
      const leftProgress = getChildProgress(state, childFilter === 'all' ? activeChildId || '' : childFilter, left.id);
      const rightProgress = getChildProgress(state, childFilter === 'all' ? activeChildId || '' : childFilter, right.id);
      if (leftProgress && rightProgress) {
        return new Date(rightProgress.learnedAt).getTime() - new Date(leftProgress.learnedAt).getTime();
      }
      if (leftProgress) return -1;
      if (rightProgress) return 1;
      return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
    });
  }, [state, type, status, childFilter, activeChildId, query]);

  const visible = filtered.slice(0, visibleCount);

  const counts = useMemo(() => {
    const result: Record<LearningStatus, number> = { unlearned: 0, learning: 0, mastered: 0 };
    state.knowledge.filter((item) => item.type === type).forEach((item) => {
      if (childFilter !== 'all') {
        const progress = getChildProgress(state, childFilter, item.id);
        result[!progress ? 'unlearned' : progress.masteredAt ? 'mastered' : 'learning'] += 1;
      } else {
        result[getItemStatus(state, item.id).status] += 1;
      }
    });
    return result;
  }, [state, type, childFilter]);

  const remove = () => {
    if (!deleteItem) return;
    updateState((current) => {
      const learningPlansByChild = current.learningPlansByChild
        ? Object.fromEntries(Object.entries(current.learningPlansByChild).map(([childId, itemIds]) => [
            childId,
            itemIds.filter((itemId) => itemId !== deleteItem.id)
          ]))
        : current.learningPlansByChild;
      return {
        ...current,
        knowledge: current.knowledge.filter((item) => item.id !== deleteItem.id),
        learningPlan: current.learningPlan.filter((itemId) => itemId !== deleteItem.id),
        learningPlansByChild,
        progress: current.progress.filter((entry) => entry.itemId !== deleteItem.id),
        hiddenKnowledgeIds: [...new Set([...(current.hiddenKnowledgeIds ?? []), deleteItem.id])],
        updatedAt: new Date().toISOString()
      };
    });
    setDeleteItem(null);
    onToast('知识已删除');
  };

  return (
    <ProfileSubPage
      title="知识库"
      subtitle="统一管理未学、学习中和已学内容"
      icon={<BookMarked size={22} />}
      onBack={() => onNavigate('menu')}
      action={
        <button className="primary-button" type="button" onClick={() => setAddOpen(true)}>
          <Plus size={18} />新增知识
        </button>
      }
    >
      <Segmented
        ariaLabel="知识类型"
        value={type}
        onChange={(value) => {
          setType(value);
          setStatus('unlearned');
        }}
        items={[
          { value: 'character', label: '汉字', icon: <span>汉</span> },
          { value: 'poem', label: '古诗', icon: <span>诗</span> },
          { value: 'word', label: '英文单词', icon: <span>A</span> }
        ]}
      />

      <div className="knowledge-filter-row">
        <Segmented
          ariaLabel="学习状态"
          value={status}
          onChange={setStatus}
          items={[
            { value: 'unlearned', label: '未学', count: counts.unlearned },
            { value: 'learning', label: '学习中', count: counts.learning },
            { value: 'mastered', label: '已学', count: counts.mastered }
          ]}
        />
        {state.children.length > 1 ? (
          <select className="compact-select" value={childFilter} onChange={(event) => setChildFilter(event.target.value)}>
            <option value="all">全部儿童</option>
            {state.children.map((child) => <option key={child.id} value={child.id}>{child.name}</option>)}
          </select>
        ) : null}
      </div>

      <div className="knowledge-search-row">
        <label className="knowledge-search">
          <Search size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={type === 'character' ? '搜索汉字、拼音或释义' : type === 'poem' ? '搜索诗名、作者或内容关键词' : '搜索英文单词或中文含义'}
          />
        </label>
        <span>显示 {Math.min(visible.length, filtered.length)} / {filtered.length}</span>
      </div>

      <div className="knowledge-grid">
        {visible.map((item) => {
          const itemStatus = getItemStatus(state, item.id);
          const planChildIds = getLearningPlanChildIds(state, item.id);
          return (
            <article className="knowledge-admin-card" key={item.id}>
              <div className={`knowledge-admin-card__visual knowledge-admin-card__visual--${item.type}`}>
                {item.type === 'character' ? <strong>{item.title}</strong> : null}
                {item.type === 'poem' ? <><span>诗</span><strong>{item.title.slice(0, 1)}</strong></> : null}
                {item.type === 'word' ? (
                  item.imageUrl ? <img src={item.imageUrl} alt="" /> : <><span>{item.emoji || 'A'}</span><strong>{item.title.slice(0, 1).toUpperCase()}</strong></>
                ) : null}
              </div>
              <div className="knowledge-admin-card__body">
                <header>
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.type === 'poem' ? `${item.dynasty || ''} · ${item.author || '佚名'}` : item.meaning || item.pronunciation || typeLabel[item.type]}</small>
                  </div>
                  <span className={`status-pill ${statusMeta[itemStatus.status].className}`}>{statusMeta[itemStatus.status].label}</span>
                </header>
                <div className="knowledge-child-progress">
                  {state.children.map((child) => {
                    const progress = getChildProgress(state, child.id, item.id);
                    const childStatus: LearningStatus = !progress ? 'unlearned' : progress.masteredAt ? 'mastered' : 'learning';
                    return (
                      <span key={child.id}>
                        <ChildAvatar child={child} size="small" />
                        <b>{child.name}</b>
                        <em className={statusMeta[childStatus].className}>{statusMeta[childStatus].label}</em>
                      </span>
                    );
                  })}
                </div>
                <div className="knowledge-meta">
                  <span><Clock3 size={15} />开始：{getLatestLearnedAt(state, item.id) || '尚未开始'}</span>
                  <span><Layers3 size={15} />进度：{getProgressSummary(state, item.id, item.type)}</span>
                  <span>
                    <ClipboardList size={15} />
                    学习计划：{planChildIds.length ? `${planChildIds.length} 人已加入` : '未加入'}
                  </span>
                  {item.source ? <span><Sparkles size={15} />来源：{item.source}</span> : null}
                </div>
                <button className="icon-button danger-text" type="button" onClick={() => setDeleteItem(item)} aria-label={`删除${item.title}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {visible.length < filtered.length ? (
        <div className="knowledge-load-more">
          <button className="secondary-button" type="button" onClick={() => setVisibleCount((count) => count + 48)}>
            加载更多
          </button>
        </div>
      ) : null}

      {!filtered.length ? (
        <div className="panel list-empty">
          <span>🌱</span>
          <strong>这个列表还是空的</strong>
          <p>可以新增知识，或切换到其他学习状态查看。</p>
        </div>
      ) : null}

      <AddKnowledgeModal
        open={addOpen}
        type={type}
        onClose={() => setAddOpen(false)}
        onAdd={(item) => {
          updateState((current) => ({
            ...current,
            knowledge: [...current.knowledge, item],
            updatedAt: new Date().toISOString()
          }));
          setStatus('unlearned');
          setAddOpen(false);
          onToast(`${typeLabel[item.type]}「${item.title}」已加入未学`);
        }}
      />

      <Modal
        open={Boolean(deleteItem)}
        title={`删除「${deleteItem?.title || ''}」`}
        subtitle="相关儿童的学习进度也会删除。"
        onClose={() => setDeleteItem(null)}
        footer={
          <>
            <button className="secondary-button" type="button" onClick={() => setDeleteItem(null)}>取消</button>
            <button className="danger-button" type="button" onClick={remove}>删除</button>
          </>
        }
      >
        <div className="danger-note"><Trash2 size={24} /><span>删除后无法恢复。</span></div>
      </Modal>
    </ProfileSubPage>
  );
}

function AddKnowledgeModal({
  open,
  type,
  onClose,
  onAdd
}: {
  open: boolean;
  type: KnowledgeType;
  onClose: () => void;
  onAdd: (item: KnowledgeItem) => void;
}) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [meaning, setMeaning] = useState('');
  const [dynasty, setDynasty] = useState('');
  const [author, setAuthor] = useState('');
  const [imageOptions, setImageOptions] = useState<string[]>([]);
  const [imageIndex, setImageIndex] = useState(0);
  const [emoji, setEmoji] = useState('🧠');
  const [loading, setLoading] = useState(false);
  const [lookupDone, setLookupDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setContent('');
    setPronunciation('');
    setMeaning('');
    setDynasty('');
    setAuthor('');
    setImageOptions([]);
    setImageIndex(0);
    setEmoji(type === 'word' ? '🧠' : '');
    setLookupDone(false);
    setError('');
  }, [open, type]);

  const searchPoem = async () => {
    if (!title.trim()) return;
    setLoading(true);
    setError('');
    const item = await lookupPoem(title.trim());
    setLoading(false);
    setLookupDone(true);
    if (!item) {
      setError('暂未找到，请检查诗名或手动补充内容。');
      return;
    }
    setTitle(item.title);
    setContent(item.content);
    setDynasty(item.dynasty || '');
    setAuthor(item.author || '');
  };

  const searchWordImage = async () => {
    if (!title.trim()) return;
    setLoading(true);
    setError('');
    const result = await lookupWordImages(title.trim());
    setLoading(false);
    setLookupDone(true);
    setImageOptions(result.images);
    setEmoji(result.fallbackEmoji || '🧠');
  };

  const submit = () => {
    if (!title.trim()) return;
    const nextContent = content.trim() || title.trim();
    onAdd({
      id: makeId(type),
      type,
      title: title.trim(),
      content: nextContent,
      pronunciation: pronunciation.trim() || undefined,
      meaning: meaning.trim() || undefined,
      dynasty: dynasty.trim() || undefined,
      author: author.trim() || undefined,
      imageUrl: imageOptions[imageIndex],
      imageOptions,
      emoji: emoji || undefined,
      source: '手动新增',
      createdAt: new Date().toISOString()
    });
  };

  return (
    <Modal
      open={open}
      title={`新增${typeLabel[type]}`}
      subtitle={
        type === 'poem'
          ? '输入诗名可联网检索原文'
          : type === 'word'
            ? '输入单词可智能匹配理解图片'
            : '输入要学习的汉字'
      }
      onClose={onClose}
      size="large"
      footer={
        <>
          <button className="secondary-button" type="button" onClick={onClose}>取消</button>
          <button className="primary-button" type="button" disabled={!title.trim()} onClick={submit}>
            <Check size={18} />确认新增
          </button>
        </>
      }
    >
      {type === 'character' ? (
        <div className="form-grid">
          <label className="form-field">
            <span>汉字</span>
            <input value={title} onChange={(event) => setTitle(event.target.value.slice(0, 1))} placeholder="例如：山" />
          </label>
          <label className="form-field">
            <span>拼音</span>
            <input value={pronunciation} onChange={(event) => setPronunciation(event.target.value)} placeholder="例如：shān" />
          </label>
          <label className="form-field form-field--wide">
            <span>释义</span>
            <input value={meaning} onChange={(event) => setMeaning(event.target.value)} placeholder="给孩子看的简单解释" />
          </label>
        </div>
      ) : null}

      {type === 'poem' ? (
        <>
          <div className="lookup-row">
            <label className="form-field">
              <span>古诗名称</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="例如：静夜思"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void searchPoem();
                }}
              />
            </label>
            <button className="secondary-button" type="button" disabled={!title.trim() || loading} onClick={() => void searchPoem()}>
              <Search size={18} />{loading ? '检索中...' : '自动搜索'}
            </button>
          </div>
          {error ? <div className="form-error">{error}</div> : null}
          {lookupDone && content ? (
            <div className="poem-lookup-preview">
              <span><Sparkles size={15} />已检索到内容，请家长核对</span>
              <h3>{title}</h3>
              <small>{dynasty ? `【${dynasty}】` : ''}{author || ''}</small>
              <p>{content}</p>
            </div>
          ) : null}
          <div className="form-grid">
            <label className="form-field">
              <span>朝代</span>
              <input value={dynasty} onChange={(event) => setDynasty(event.target.value)} placeholder="例如：唐" />
            </label>
            <label className="form-field">
              <span>作者</span>
              <input value={author} onChange={(event) => setAuthor(event.target.value)} placeholder="例如：李白" />
            </label>
            <label className="form-field form-field--wide">
              <span>古诗内容</span>
              <textarea value={content} onChange={(event) => setContent(event.target.value)} rows={6} placeholder="每句一行" />
            </label>
          </div>
        </>
      ) : null}

      {type === 'word' ? (
        <>
          <div className="lookup-row">
            <label className="form-field">
              <span>英文单词</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value.replace(/[^a-zA-Z\s-]/g, ''))}
                placeholder="例如：apple"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void searchWordImage();
                }}
              />
            </label>
            <button className="secondary-button" type="button" disabled={!title.trim() || loading} onClick={() => void searchWordImage()}>
              <Sparkles size={18} />{loading ? '生成中...' : '智能配图'}
            </button>
          </div>
          <div className="word-image-choice">
            {imageOptions.length ? (
              <img src={imageOptions[imageIndex]} alt={`${title} 的候选图片`} />
            ) : (
              <div className="image-fallback"><span>{emoji}</span><strong>{title || 'word'}</strong><small>可手动补充中文含义</small></div>
            )}
            <div>
              <button
                className="secondary-button"
                type="button"
                disabled={!imageOptions.length}
                onClick={() => setImageIndex((value) => (value + 1) % imageOptions.length)}
              >
                <RefreshCcw size={17} />换一张
              </button>
              <span>{imageOptions.length ? `${imageIndex + 1} / ${imageOptions.length}` : '内置图形知识库'}</span>
            </div>
          </div>
          <div className="form-grid">
            <label className="form-field">
              <span>中文含义</span>
              <input value={meaning} onChange={(event) => setMeaning(event.target.value)} placeholder="例如：苹果" />
            </label>
            <label className="form-field">
              <span>音标/读音提示</span>
              <input value={pronunciation} onChange={(event) => setPronunciation(event.target.value)} placeholder="例如：/ˈæpəl/" />
            </label>
          </div>
        </>
      ) : null}
    </Modal>
  );
}

function StarShop({ state, activeChildId, updateState, onNavigate, onToast }: ProfileProps) {
  const selected = state.children.find((child) => child.id === activeChildId) || state.children[0];
  const [confirmProduct, setConfirmProduct] = useState<Product | null>(null);
  const products = state.products.filter((product) => product.active);

  if (!selected) {
    return <ProfileSubPage title="星星商店" subtitle="先添加儿童后才能兑换" icon={<Store size={22} />} onBack={() => onNavigate('menu')}><div className="list-empty panel">请先添加儿童。</div></ProfileSubPage>;
  }

  const redeem = () => {
    if (!confirmProduct) return;
    if (selected.stars < confirmProduct.cost) {
      onToast(`还差 ${confirmProduct.cost - selected.stars} 颗星星`);
      setConfirmProduct(null);
      return;
    }
    updateState((current) => ({
      ...current,
      children: current.children.map((child) =>
        child.id === selected.id ? { ...child, stars: child.stars - confirmProduct.cost } : child
      ),
      exchanges: [
        {
          id: makeId('exchange'),
          childId: selected.id,
          productId: confirmProduct.id,
          productName: confirmProduct.name,
          icon: confirmProduct.icon,
          cost: confirmProduct.cost,
          exchangedAt: new Date().toISOString()
        },
        ...current.exchanges
      ],
      updatedAt: new Date().toISOString()
    }));
    onToast(`兑换成功，找爸爸妈妈获取${confirmProduct.name}吧！`);
    setConfirmProduct(null);
  };

  return (
    <ProfileSubPage
      title="星星商店"
      subtitle="认真积累，把星星换成期待的小奖励"
      icon={<Store size={22} />}
      onBack={() => onNavigate('menu')}
      action={<button className="secondary-button" type="button" onClick={() => onNavigate('shopRecords')}><ChartColumn size={18} />兑换记录</button>}
    >
      {state.children.length > 1 ? (
        <div className="shop-child-tabs">
          {state.children.map((child) => (
            <button
              key={child.id}
              className={child.id === selected.id ? 'is-active' : ''}
              type="button"
              onClick={() => updateState((current) => ({ ...current, activeChildId: child.id }))}
            >
              <ChildAvatar child={child} size="small" active={child.id === selected.id} />
              <span><strong>{child.name}</strong><small>{child.stars} 颗星星</small></span>
            </button>
          ))}
        </div>
      ) : (
        <div className="single-star-balance"><Star size={24} fill="currentColor" /><strong>{selected.stars}</strong><span>颗星星可用</span></div>
      )}
      <div className="shop-grid">
        {products.map((product) => {
          const affordable = selected.stars >= product.cost;
          return (
            <article className="product-card" key={product.id}>
              <div className="product-card__icon">{product.icon}</div>
              <strong>{product.name}</strong>
              <span><Star size={16} fill="currentColor" />{product.cost}</span>
              <button
                className={affordable ? 'primary-button' : 'secondary-button'}
                type="button"
                onClick={() => setConfirmProduct(product)}
              >
                {affordable ? '兑换' : '星星不够'}
              </button>
            </article>
          );
        })}
      </div>

      <Modal
        open={Boolean(confirmProduct)}
        title="确认兑换"
        subtitle={`使用 ${selected.name} 的星星兑换商品`}
        onClose={() => setConfirmProduct(null)}
        footer={
          <>
            <button className="secondary-button" type="button" onClick={() => setConfirmProduct(null)}>再想想</button>
            <button className="primary-button" type="button" onClick={redeem}>确认兑换</button>
          </>
        }
      >
        <div className="exchange-confirm">
          <span>{confirmProduct?.icon}</span>
          <div><strong>{confirmProduct?.name}</strong><small>将扣除 {confirmProduct?.cost} 颗星星，剩余 {Math.max(0, selected.stars - (confirmProduct?.cost || 0))} 颗</small></div>
        </div>
      </Modal>
    </ProfileSubPage>
  );
}

function ShopRecords({ state, activeChildId, onSelectChild, onNavigate }: ProfileProps) {
  const selected = state.children.find((child) => child.id === activeChildId) || state.children[0];
  const records = state.exchanges.filter((record) => record.childId === selected?.id).sort((a, b) => new Date(b.exchangedAt).getTime() - new Date(a.exchangedAt).getTime());
  const totalCost = records.reduce((sum, record) => sum + record.cost, 0);
  const ranking = Object.values(records.reduce<Record<string, { name: string; icon: string; count: number; cost: number }>>((result, record) => {
    result[record.productId] ||= { name: record.productName, icon: record.icon, count: 0, cost: 0 };
    result[record.productId].count += 1;
    result[record.productId].cost += record.cost;
    return result;
  }, {})).sort((a, b) => b.count - a.count || b.cost - a.cost);

  return (
    <ProfileSubPage
      title="兑换记录"
      subtitle="查看每个孩子的星星消费情况"
      icon={<ChartColumn size={22} />}
      onBack={() => onNavigate('menu')}
    >
      {state.children.length > 1 ? (
        <div className="shop-child-tabs">
          {state.children.map((child) => (
            <button
              key={child.id}
              className={child.id === selected?.id ? 'is-active' : ''}
              type="button"
              onClick={() => onSelectChild(child.id)}
            >
              <ChildAvatar child={child} size="small" active={child.id === selected?.id} />
              <span><strong>{child.name}</strong><small>{child.stars} 颗星星</small></span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="record-metrics">
        <article><small>兑换次数</small><strong>{records.length}</strong></article>
        <article><small>累计消费</small><strong>{totalCost}<em> 星</em></strong></article>
        <article><small>最爱兑换</small><strong>{ranking[0]?.name || '暂无'}</strong></article>
      </div>
      <div className="records-layout">
        <section className="panel records-list">
          <SectionTitle title="兑换明细" eyebrow={selected?.name || ''} />
          {records.map((record) => (
            <article key={record.id}>
              <span>{record.icon}</span>
              <div><strong>{record.productName}</strong><small>{formatDateTime(record.exchangedAt)}</small></div>
              <b>-{record.cost} <Star size={14} fill="currentColor" /></b>
            </article>
          ))}
          {!records.length ? <div className="mini-empty">还没有兑换记录</div> : null}
        </section>
        <section className="panel ranking-list">
          <SectionTitle title="商品兑换排行" eyebrow="按次数排序" />
          {ranking.map((entry, index) => (
            <article key={entry.name}>
              <span>{index + 1}</span>
              <em>{entry.icon}</em>
              <div><strong>{entry.name}</strong><small>共 {entry.count} 次 · {entry.cost} 星</small></div>
            </article>
          ))}
          {!ranking.length ? <div className="mini-empty">暂无排行数据</div> : null}
        </section>
      </div>
    </ProfileSubPage>
  );
}

function SettingsPage({ onNavigate, onLoginChange, onToast }: ProfileProps) {
  const items: Array<{ page: ProfilePage; title: string; note: string; icon: typeof Settings; tone: string }> = [
    { page: 'children', title: '儿童管理', note: '新增、修改和删除儿童', icon: Baby, tone: 'coral' },
    { page: 'curves', title: '遗忘曲线设置', note: '分别设置汉字、古诗和英文间隔', icon: RefreshCcw, tone: 'blue' },
    { page: 'rewards', title: '星星获得设置', note: '设置认识、忘记和完成奖励', icon: Star, tone: 'gold' },
    { page: 'shopSettings', title: '星星商店设置', note: '商品上架、下架和价格修改', icon: Store, tone: 'mint' }
  ];
  return (
    <ProfileSubPage title="设置" subtitle="按照孩子的节奏调整记忆计划" icon={<Settings size={22} />} onBack={() => onNavigate('menu')}>
      <section className="panel settings-list">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <button key={item.page} type="button" onClick={() => onNavigate(item.page)}>
              <span className={`profile-menu__icon profile-menu__icon--${item.tone}`}><Icon size={21} /></span>
              <span><strong>{item.title}</strong><small>{item.note}</small></span>
              <ChevronRight size={19} />
            </button>
          );
        })}
      </section>
      <section className="panel settings-account">
        <div><ShieldCheck size={23} /><span><strong>当前账号</strong><small>admin · 家长管理员</small></span></div>
        <button className="danger-button" type="button" onClick={() => {
          onLoginChange(false);
          onToast('已退出登录');
        }}><LogOut size={18} />退出登录</button>
      </section>
    </ProfileSubPage>
  );
}

function CurveSettings({ state, updateState, onToast, onNavigate }: ProfileProps) {
  const [values, setValues] = useState(() => ({
    character: state.settings.intervals.character.join(', '),
    poem: state.settings.intervals.poem.join(', '),
    word: state.settings.intervals.word.join(', ')
  }));

  const save = () => {
    const parsed = {
      character: parseIntervals(values.character),
      poem: parseIntervals(values.poem),
      word: parseIntervals(values.word)
    };
    if (Object.values(parsed).some((list) => !list.length || list.some((value) => value < 0) || !list.includes(0))) {
      onToast('间隔需为非负数，并包含起始值 0');
      return;
    }
    updateState((current) => ({
      ...current,
      settings: {
        ...current.settings,
        intervals: parsed
      },
      updatedAt: new Date().toISOString()
    }));
    onToast('遗忘曲线设置已保存');
  };

  return (
    <ProfileSubPage
      title="遗忘曲线设置"
      subtitle="数字表示学习后的第几天复习"
      icon={<RefreshCcw size={22} />}
      onBack={() => onNavigate('menu')}
      action={<button className="primary-button" type="button" onClick={save}><Save size={18} />保存设置</button>}
    >
      <div className="curve-note">
        <Sparkles size={20} />
        <span><strong>默认节奏：当天、1 天后、2 天后、4 天后、7 天后</strong><small>修改只影响后续新产生的复习计划，已保存的历史记录不会丢失。</small></span>
      </div>
      <div className="curve-grid">
        {(Object.keys(typeLabel) as KnowledgeType[]).map((type) => (
          <article className={`curve-card curve-card--${type}`} key={type}>
            <header>
              <span>{type === 'character' ? '汉' : type === 'poem' ? '诗' : 'A'}</span>
              <div><strong>{typeLabel[type]}</strong><small>{parseIntervals(values[type]).length || 0} 个复习节点</small></div>
            </header>
            <label>
              <span>复习间隔（天）</span>
              <input value={values[type]} onChange={(event) => setValues((current) => ({ ...current, [type]: event.target.value }))} />
            </label>
            <div className="interval-preview">
              {parseIntervals(values[type]).map((value, index) => (
                <span key={`${value}-${index}`}>{index === 0 ? '首次' : `第 ${index + 1} 次`}<b>{value} 天</b></span>
              ))}
            </div>
          </article>
        ))}
      </div>
      <button className="text-button reset-button" type="button" onClick={() => setValues({ character: '0, 1, 2, 4, 7', poem: '0, 1, 2, 4, 7', word: '0, 1, 2, 4, 7' })}>
        <RefreshCcw size={16} />恢复默认曲线
      </button>
    </ProfileSubPage>
  );
}

function RewardSettings({ state, updateState, onToast, onNavigate }: ProfileProps) {
  const [values, setValues] = useState(state.settings.rewards);

  const update = (type: KnowledgeType, field: keyof RewardConfig, value: string) => {
    setValues((current) => ({
      ...current,
      [type]: { ...current[type], [field]: Number(value) || 0 }
    }));
  };

  return (
    <ProfileSubPage
      title="星星获得设置"
      subtitle="认识加星、忘记减星，可分别设置三类知识"
      icon={<Star size={22} />}
      onBack={() => onNavigate('menu')}
      action={
        <button className="primary-button" type="button" onClick={() => {
          updateState((current) => ({
            ...current,
            settings: { ...current.settings, rewards: values },
            updatedAt: new Date().toISOString()
          }));
          onToast('星星奖励设置已保存');
        }}><Save size={18} />保存设置</button>
      }
    >
      <div className="reward-grid">
        {(Object.keys(typeLabel) as KnowledgeType[]).map((type) => (
          <article className={`reward-card reward-card--${type}`} key={type}>
            <header><span>{type === 'character' ? '汉' : type === 'poem' ? '诗' : 'A'}</span><strong>{typeLabel[type]}</strong></header>
            <label><span>选择「认识」</span><div><b>+</b><input type="number" value={values[type].known} onChange={(event) => update(type, 'known', event.target.value)} /></div></label>
            <label><span>选择「忘记」</span><div><b>±</b><input type="number" value={values[type].forgot} onChange={(event) => update(type, 'forgot', event.target.value)} /></div></label>
            <label><span>完成全部记忆</span><div><b>+</b><input type="number" value={values[type].mastered} onChange={(event) => update(type, 'mastered', event.target.value)} /></div></label>
          </article>
        ))}
      </div>
    </ProfileSubPage>
  );
}

function ShopSettings({ state, updateState, onToast, onNavigate }: ProfileProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);

  return (
    <ProfileSubPage
      title="星星商店设置"
      subtitle="管理上架商品和兑换星星价格"
      icon={<Store size={22} />}
      onBack={() => onNavigate('menu')}
      action={<button className="primary-button" type="button" onClick={() => setCreateOpen(true)}><PackagePlus size={18} />上架商品</button>}
    >
      <div className="admin-product-grid">
        {state.products.map((product) => (
          <article className={`admin-product-card ${product.active ? '' : 'is-offline'}`} key={product.id}>
            <div className="admin-product-card__icon">{product.icon}</div>
            <div className="admin-product-card__body">
              <header><strong>{product.name}</strong><span>{product.active ? '上架中' : '已下架'}</span></header>
              <p><Star size={15} fill="currentColor" />{product.cost} 颗星星</p>
              <div>
                <button className="secondary-button" type="button" onClick={() => setEditProduct(product)}><Pencil size={16} />修改星星</button>
                <button className="secondary-button" type="button" onClick={() => {
                  updateState((current) => ({
                    ...current,
                    products: current.products.map((item) => item.id === product.id ? { ...item, active: !item.active } : item),
                    updatedAt: new Date().toISOString()
                  }));
                  onToast(product.active ? '商品已下架' : '商品已上架');
                }}>{product.active ? '下架' : '上架'}</button>
                {!product.active ? (
                  <button className="icon-button danger-text" type="button" onClick={() => setDeleteProduct(product)} aria-label="删除商品"><Trash2 size={18} /></button>
                ) : null}
              </div>
            </div>
          </article>
        ))}
      </div>

      <ProductCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={(product) => {
          updateState((current) => ({
            ...current,
            products: [product, ...current.products],
            updatedAt: new Date().toISOString()
          }));
          setCreateOpen(false);
          onToast('商品已上架');
        }}
      />

      <ProductPriceModal
        product={editProduct}
        onClose={() => setEditProduct(null)}
        onSave={(cost) => {
          if (!editProduct) return;
          updateState((current) => ({
            ...current,
            products: current.products.map((item) => item.id === editProduct.id ? { ...item, cost } : item),
            updatedAt: new Date().toISOString()
          }));
          setEditProduct(null);
          onToast('商品星星数量已修改');
        }}
      />

      <Modal
        open={Boolean(deleteProduct)}
        title={`删除「${deleteProduct?.name || ''}」`}
        subtitle="已下架商品可以永久删除。"
        onClose={() => setDeleteProduct(null)}
        footer={
          <>
            <button className="secondary-button" type="button" onClick={() => setDeleteProduct(null)}>取消</button>
            <button className="danger-button" type="button" onClick={() => {
              if (!deleteProduct) return;
              updateState((current) => ({
                ...current,
                products: current.products.filter((item) => item.id !== deleteProduct.id),
                updatedAt: new Date().toISOString()
              }));
              setDeleteProduct(null);
              onToast('商品已删除');
            }}>确认删除</button>
          </>
        }
      >
        <div className="danger-note"><Trash2 size={24} /><span>历史兑换记录仍会保留。</span></div>
      </Modal>
    </ProfileSubPage>
  );
}

function ProductCreateModal({
  open,
  onClose,
  onSave
}: {
  open: boolean;
  onClose: () => void;
  onSave: (product: Product) => void;
}) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('🎁');
  const [cost, setCost] = useState(50);

  useEffect(() => {
    if (!open) return;
    setName('');
    setIcon('🎁');
    setCost(50);
  }, [open]);

  const generateIcon = (productName = name) => {
    const matched = Object.entries(productIcons).find(([keyword]) => productName.includes(keyword));
    const choices = Object.values(productIcons);
    setIcon(matched?.[1] || choices[Math.floor(Math.random() * choices.length)]);
  };

  return (
    <Modal
      open={open}
      title="上架新商品"
      subtitle="输入名称后可智能生成商品图标"
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button" type="button" onClick={onClose}>取消</button>
          <button className="primary-button" type="button" disabled={!name.trim() || cost <= 0} onClick={() => onSave({
            id: makeId('product'),
            name: name.trim(),
            icon,
            cost,
            active: true,
            createdAt: new Date().toISOString()
          })}>确认上架</button>
        </>
      }
    >
      <div className="product-creator">
        <div className="product-creator__preview">{icon}</div>
        <button className="secondary-button" type="button" onClick={() => generateIcon()}><Sparkles size={17} />换一个图标</button>
      </div>
      <div className="form-grid">
        <label className="form-field form-field--wide">
          <span>商品名称</span>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：周末去公园野餐" />
        </label>
        <label className="form-field">
          <span>所需星星</span>
          <input type="number" min="1" value={cost} onChange={(event) => setCost(Number(event.target.value))} />
        </label>
      </div>
    </Modal>
  );
}

function ProductPriceModal({
  product,
  onClose,
  onSave
}: {
  product: Product | null;
  onClose: () => void;
  onSave: (cost: number) => void;
}) {
  const [cost, setCost] = useState(0);
  useEffect(() => setCost(product?.cost || 0), [product]);
  return (
    <Modal
      open={Boolean(product)}
      title="修改星星数量"
      subtitle={product ? `商品：${product.name}` : ''}
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button" type="button" onClick={onClose}>取消</button>
          <button className="primary-button" type="button" disabled={cost <= 0} onClick={() => onSave(cost)}>保存</button>
        </>
      }
    >
      <label className="form-field">
        <span>所需星星</span>
        <input type="number" min="1" value={cost} onChange={(event) => setCost(Number(event.target.value))} />
      </label>
    </Modal>
  );
}

function ProfileSubPage({
  title,
  subtitle,
  icon,
  onBack,
  action,
  children
}: {
  title: string;
  subtitle: string;
  icon: ReactNode;
  onBack: () => void;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="subpage">
      <header className="subpage__header">
        <button className="icon-button" type="button" onClick={onBack} aria-label="返回">
          <ArrowLeft size={21} />
        </button>
        <span className="subpage__icon">{icon}</span>
        <div><h1>{title}</h1><p>{subtitle}</p></div>
        <div className="subpage__action">{action}</div>
      </header>
      <div className="subpage__content">{children}</div>
    </section>
  );
}

function parseIntervals(value: string) {
  return [...new Set(value.split(/[,，\s]+/).map((entry) => Number(entry)).filter((entry) => Number.isFinite(entry)))]
    .sort((a, b) => a - b);
}

function getLatestLearnedAt(state: AppState, itemId: string) {
  const dates = state.progress
    .filter((entry) => entry.itemId === itemId)
    .map((entry) => entry.learnedAt)
    .sort();
  return dates[0] ? formatDateTime(dates[0]) : '';
}

function getProgressSummary(state: AppState, itemId: string, type: KnowledgeType) {
  const entries = state.progress.filter((entry) => entry.itemId === itemId);
  if (!entries.length) return '0 / 2 人已开始';
  const total = state.settings.intervals[type].length;
  const mastered = entries.filter((entry) => entry.masteredAt).length;
  const averageStage = entries.reduce((sum, entry) => sum + entry.stage / total, 0) / state.children.length;
  return `${mastered}/${state.children.length} 人已完成 · 平均 ${Math.round(averageStage * 100)}%`;
}
